import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Checkbox } from '@/components/ui/checkbox'
import { ArrowLeft, Plus, Trash2, User } from 'lucide-react'
import {
  getClientDetailsV2,
  updateClientV2,
  updateClientBillingAddressV2,
  type ClientDetailsV2,
  type UpdateClientRequestV2,
} from '@/lib/api/v2/clients'
import {
  getCatalogCivilitiesV2,
  getCatalogCustomerTypesV2,
  getCatalogLanguagesV2,
  type CatalogItemDto,
} from '@/lib/api/v2/catalog'
import { showErrorToast } from '@/lib/toast'
import { formatClientName } from '@/lib/format'

const contactSchema = z.object({
  id: z.string().nullable().optional(),
  isPrimary: z.boolean().optional(),
  civility: z.number().nullable().optional(),
  firstName: z.string().optional(),
  lastName: z.string().min(1, 'Nom requis'),
  phone: z.string().optional(),
  email: z.string().email('Email invalide').optional().or(z.literal('')),
  includeInCommunications: z.boolean().optional(),
})

const addressSchema = z.object({
  street: z.string().optional(),
  number: z.string().optional(),
  box: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
})

const clientSchema = z.object({
  type: z.number(),
  name: z.string().min(1, 'Nom requis'),
  designation: z.string().optional(),
  vatNumber: z.string().optional(),
  companyRegistrationNumber: z.string().optional(),
  glnNumber: z.string().optional(),
  preferredLanguageId: z.string().optional(),
  contacts: z.array(contactSchema).min(1, 'Au moins un contact requis'),
  billingAddress: addressSchema.optional(),
})

type ClientFormData = z.infer<typeof clientSchema>

export default function ClientEditPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { customerId } = useParams<{ customerId: string }>()

  const [client, setClient] = useState<ClientDetailsV2 | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  // Metadata
  const [customerTypes, setCustomerTypes] = useState<CatalogItemDto[]>([])
  const [languages, setLanguages] = useState<CatalogItemDto[]>([])
  const [civilities, setCivilities] = useState<CatalogItemDto[]>([])

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isValid },
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    mode: 'onChange',
    defaultValues: {
      type: 1,
      name: '',
      designation: '',
      vatNumber: '',
      companyRegistrationNumber: '',
      glnNumber: '',
      preferredLanguageId: '',
      contacts: [],
      billingAddress: {
        street: '',
        number: '',
        box: '',
        postalCode: '',
        city: '',
        country: 'BE',
      },
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'contacts',
  })

  const selectedType = watch('type')
  const isCompany = selectedType === 2 // Assuming 2 = company, 1 = individual

  // Load client data and metadata
  useEffect(() => {
    if (!customerId) {
      navigate('/home')
      return
    }

    async function loadData() {
      try {
        const [clientData, types, langs, civs] = await Promise.all([
          getClientDetailsV2(customerId!),
          getCatalogCustomerTypesV2(),
          getCatalogLanguagesV2(),
          getCatalogCivilitiesV2(),
        ])

        if (!clientData) {
          navigate('/home')
          return
        }

        setClient(clientData)
        setCustomerTypes(types)
        setLanguages(langs)
        setCivilities(civs)

        // Set form values
        setValue('type', clientData.type || 1)
        setValue('name', clientData.name || '')
        setValue('designation', clientData.designation || '')
        setValue('vatNumber', clientData.vatNumber || '')
        setValue('companyRegistrationNumber', clientData.companyRegistrationNumber || '')
        setValue('glnNumber', clientData.glnNumber || '')
        setValue('preferredLanguageId', clientData.preferredLanguageId || '')

        // Set contacts
        if (clientData.contacts && clientData.contacts.length > 0) {
          setValue(
            'contacts',
            clientData.contacts.map((c) => ({
              id: c.id || null,
              isPrimary: c.isPrimary || false,
              civility: c.civility || null,
              firstName: c.firstName || '',
              lastName: c.lastName || '',
              phone: c.phone || '',
              email: c.email || '',
              includeInCommunications: c.includeInCommunications ?? true,
            }))
          )
        }

        // Set billing address
        if (clientData.billingAddress?.details) {
          const addr = clientData.billingAddress.details
          setValue('billingAddress', {
            street: addr.street || '',
            number: addr.number || '',
            box: addr.box || '',
            postalCode: addr.postalCode || '',
            city: addr.city || '',
            country: addr.country || 'BE',
          })
        }
      } catch (error) {
        console.error('Error loading client data:', error)
        showErrorToast('Erreur de chargement')
        navigate('/home')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [customerId, navigate, setValue])

  const onSubmit = async (data: ClientFormData) => {
    if (!customerId) return

    setIsSaving(true)
    try {
      // Update client details
      const updatePayload: UpdateClientRequestV2 = {
        type: data.type,
        name: data.name,
        designation: data.type !== 2 ? (data.designation || null) : null,
        vatNumber: data.vatNumber || null,
        companyRegistrationNumber: data.companyRegistrationNumber || null,
        glnNumber: data.glnNumber || null,
        preferredLanguageId: data.preferredLanguageId || null,
        contacts: data.contacts.map((c) => ({
          id: c.id || null,
          isPrimary: c.isPrimary || false,
          civility: c.civility || null,
          firstName: c.firstName || null,
          lastName: c.lastName || null,
          phone: c.phone || null,
          email: c.email || null,
          // Primary contact always included in communications
          includeInCommunications: c.isPrimary ? true : (c.includeInCommunications ?? true),
        })),
      }

      await updateClientV2(customerId, updatePayload)

      // Update billing address if changed
      if (data.billingAddress) {
        const hasAddress =
          data.billingAddress.street ||
          data.billingAddress.number ||
          data.billingAddress.postalCode ||
          data.billingAddress.city

        if (hasAddress) {
          await updateClientBillingAddressV2(customerId, {
            street: data.billingAddress.street || null,
            number: data.billingAddress.number || null,
            box: data.billingAddress.box || null,
            postalCode: data.billingAddress.postalCode || null,
            city: data.billingAddress.city || null,
            country: data.billingAddress.country || 'BE',
          })
        }
      }

      // Navigate back
      handleBack()
    } catch (error) {
      console.error('Error updating client:', error)
      showErrorToast('Erreur de sauvegarde')
    } finally {
      setIsSaving(false)
    }
  }

  const handleBack = () => {
    const from = (location.state as { from?: string } | null)?.from
    if (from) {
      navigate(from)
    } else {
      navigate(`/clients/${customerId}`)
    }
  }

  const handleAddContact = () => {
    append({
      id: null,
      isPrimary: false,
      civility: null,
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      includeInCommunications: true,
    })
  }

  const handleRemoveContact = (index: number) => {
    const contact = fields[index]
    if (contact.isPrimary) {
      showErrorToast('Impossible de supprimer le contact principal')
      return
    }
    remove(index)
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Chargement...</div>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  if (!client) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12 text-muted-foreground">Client introuvable</div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Modifier le client</h1>
            <p className="text-sm text-muted-foreground">{client.name}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Client Type & Basic Info */}
          <div className="space-y-3">
            <h2 className="text-base font-semibold">Informations générales</h2>

            <div className="grid grid-cols-3 gap-3">
              {/* Client Type */}
              <div className="space-y-2">
                <Label>Type de client *</Label>
                <Select
                  value={selectedType ? String(selectedType) : undefined}
                  onValueChange={(v) => setValue('type', Number(v), { shouldValidate: true })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {customerTypes.map((type) => (
                      <SelectItem key={type.id} value={String(type.id)}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.type && <p className="text-sm text-red-600">{errors.type.message}</p>}
              </div>

              {/* Preferred Language */}
              <div className="space-y-2">
                <Label>Langue préférée</Label>
                <Select
                  value={watch('preferredLanguageId') || ''}
                  onValueChange={(v) => setValue('preferredLanguageId', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map((lang) => (
                      <SelectItem key={lang.id} value={lang.id!}>
                        {lang.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  La langue par défaut s'applique aux futurs projets de ce client. Les
                  projets existants conservent leur langue.
                </p>
              </div>

              {/* Designation + Name — individuals */}
              {!isCompany && (
                <div className="col-span-3 rounded-lg border bg-muted/20 p-4 space-y-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Nom du client — tel qu'il apparaît sur les documents
                  </p>

                  {/* Quick-pick chips */}
                  <div className="flex gap-2 flex-wrap">
                    {['M. & Mme', 'M.', 'Mme', 'M. & M.', 'Mme & Mme'].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setValue('designation', watch('designation') === opt ? '' : opt, { shouldValidate: true })}
                        className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                          watch('designation') === opt
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border hover:border-primary'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>

                  {/* Designation + Name side by side */}
                  <div className="grid grid-cols-[160px_1fr] gap-3 items-end">
                    <div className="space-y-1">
                      <Label htmlFor="designation" className="text-xs">Désignation</Label>
                      <Input
                        id="designation"
                        {...register('designation')}
                        placeholder="M. & Mme"
                        data-testid="client-designation-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="name" className="text-xs">Nom *</Label>
                      <Input id="name" {...register('name')} placeholder="Dupont" />
                      {errors.name && <p className="text-xs text-red-600">{errors.name.message}</p>}
                    </div>
                  </div>

                  {/* Live preview */}
                  {(watch('designation') || watch('name')) && (
                    <p className="text-xs text-muted-foreground">
                      Sur les documents :{' '}
                      <span className="font-medium text-foreground">
                        {formatClientName(watch('name'), watch('designation'))}
                      </span>
                    </p>
                  )}
                </div>
              )}

              {/* Name — company */}
              {isCompany && (
                <div className="space-y-2 col-span-3">
                  <Label htmlFor="name">Nom de la société *</Label>
                  <Input id="name" {...register('name')} placeholder="Ex: SPRL Dupont" />
                  {errors.name && <p className="text-sm text-red-600">{errors.name.message}</p>}
                  <p className="text-xs text-muted-foreground">Ce nom apparaîtra sur tous les documents</p>
                </div>
              )}

            {/* Company-specific fields */}
            {isCompany && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="vatNumber">Numéro de TVA</Label>
                  <Input id="vatNumber" {...register('vatNumber')} placeholder="BE 0123.456.789" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyRegistrationNumber">Numéro d'entreprise</Label>
                  <Input
                    id="companyRegistrationNumber"
                    {...register('companyRegistrationNumber')}
                    placeholder="0123.456.789"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="glnNumber">Numéro GLN</Label>
                  <Input
                    id="glnNumber"
                    {...register('glnNumber')}
                    placeholder="1234567890123"
                  />
                </div>
              </>
            )}
            </div>
          </div>

          <Separator className="my-3" />

          {/* Contacts */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold">Contacts</h2>
              <Button type="button" variant="outline" size="sm" onClick={handleAddContact}>
                <Plus className="h-4 w-4 mr-2" />
                Ajouter un contact
              </Button>
            </div>

            {fields.map((field, index) => {
              const isPrimary = field.isPrimary
              return (
                <div key={field.id} className="p-3 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        Contact {index + 1}
                        {isPrimary && <span className="ml-2 text-xs text-primary">(Principal)</span>}
                      </span>
                    </div>
                    {!isPrimary && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveContact(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-4 gap-3">
                    {/* Civility */}
                    <div className="space-y-2 col-span-1">
                      <Label className="text-sm">Civ.</Label>
                      <Select
                        value={watch(`contacts.${index}.civility`) != null ? String(watch(`contacts.${index}.civility`)) : undefined}
                        onValueChange={(v) => setValue(`contacts.${index}.civility`, Number(v))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          {civilities.map((civ) => (
                            <SelectItem key={civ.id} value={String(civ.id)}>
                              {civ.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* First Name */}
                    <div className="space-y-2 col-span-1">
                      <Label className="text-sm">Prénom</Label>
                      <Input {...register(`contacts.${index}.firstName`)} placeholder="Jean" />
                    </div>

                    {/* Last Name */}
                    <div className="space-y-2 col-span-2">
                      <Label className="text-sm">Nom *</Label>
                      <Input {...register(`contacts.${index}.lastName`)} placeholder="Dupont" />
                      {errors.contacts?.[index]?.lastName && (
                        <p className="text-sm text-red-600">{errors.contacts[index]?.lastName?.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {/* Phone */}
                    <div className="space-y-2">
                      <Label className="text-sm">Téléphone</Label>
                      <Input {...register(`contacts.${index}.phone`)} placeholder="+32 123 45 67 89" />
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label className="text-sm">Email</Label>
                      <Input
                        type="email"
                        {...register(`contacts.${index}.email`)}
                        placeholder="jean.dupont@example.com"
                      />
                      {errors.contacts?.[index]?.email && (
                        <p className="text-sm text-red-600">{errors.contacts[index]?.email?.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Include in communications - hidden for primary contact */}
                  {!isPrimary && (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`contact-${index}-comm`}
                        checked={watch(`contacts.${index}.includeInCommunications`) ?? true}
                        onCheckedChange={(checked) =>
                          setValue(`contacts.${index}.includeInCommunications`, checked === true)
                        }
                      />
                      <Label htmlFor={`contact-${index}-comm`} className="text-sm font-normal cursor-pointer">
                        Inclure dans les communications (emails, notifications)
                      </Label>
                    </div>
                  )}
                </div>
              )
            })}

            {errors.contacts && typeof errors.contacts === 'object' && 'message' in errors.contacts && (
              <p className="text-sm text-red-600">{errors.contacts.message as string}</p>
            )}
          </div>

          <Separator className="my-3" />

          {/* Billing Address */}
          <div className="space-y-3">
            <h2 className="text-base font-semibold">Adresse de facturation</h2>

            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-2 col-span-2">
                <Label className="text-sm">Rue</Label>
                <Input {...register('billingAddress.street')} placeholder="Rue du Marché" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Numéro</Label>
                <Input {...register('billingAddress.number')} placeholder="140" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Boîte</Label>
                <Input {...register('billingAddress.box')} placeholder="A" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Code postal</Label>
                <Input {...register('billingAddress.postalCode')} placeholder="1370" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-sm">Ville</Label>
                <Input {...register('billingAddress.city')} placeholder="Jodoigne" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Pays</Label>
                <Input {...register('billingAddress.country')} placeholder="BE" />
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t sticky bottom-0 bg-background">
            <Button type="button" variant="outline" onClick={handleBack} disabled={isSaving}>
              Annuler
            </Button>
            <Button type="submit" disabled={!isValid || isSaving}>
              {isSaving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  )
}
